import Image from "next/image";
import type { ReactNode } from "react";
import { Target, Sparkles, CalendarClock, BarChart3, Gauge } from "lucide-react";
import { SiHubspot, SiGooglesheets, SiFacebook } from "react-icons/si";

interface NodePosition {
  icon: ReactNode;
  x: number;
  y: number;
}

const NODE_POSITIONS: NodePosition[] = [
  { icon: <Target size={15} />, x: 50, y: 12 },
  { icon: <Sparkles size={15} />, x: 79.5, y: 26 },
  { icon: <CalendarClock size={15} />, x: 92, y: 50 },
  { icon: <SiHubspot size={13} />, x: 79.5, y: 74 },
  { icon: <SiGooglesheets size={13} />, x: 50, y: 88 },
  { icon: <SiFacebook size={13} />, x: 20.5, y: 74 },
  { icon: <BarChart3 size={15} />, x: 8, y: 50 },
  { icon: <Gauge size={15} />, x: 20.5, y: 26 },
];

export default function AgentBrainMap({
  centerName,
  labels,
}: {
  centerName: string;
  labels: readonly string[];
}) {
  const nodes = NODE_POSITIONS.map((pos, i) => ({ ...pos, label: labels[i] }));

  return (
    <div className="relative mx-auto aspect-square w-full max-w-xl">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" preserveAspectRatio="none" aria-hidden="true">
        {nodes.map((node) => (
          <line
            key={node.label}
            x1={50}
            y1={50}
            x2={node.x}
            y2={node.y}
            stroke="var(--af-border)"
            strokeWidth={0.4}
          />
        ))}
      </svg>

      {/* Center employee */}
      <div
        className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
        style={{ left: "50%", top: "50%" }}
      >
        <div className="af-breathe relative h-16 w-16 overflow-hidden rounded-full ring-4 ring-[var(--af-bg)] sm:h-20 sm:w-20" style={{ boxShadow: "var(--af-shadow)" }}>
          <Image src="/avatars/marlaa.png" alt={centerName} fill sizes="80px" className="object-cover object-top" />
        </div>
        <span className="whitespace-nowrap rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] px-2.5 py-1 text-[11px] font-medium text-[var(--af-text)]">
          {centerName}
        </span>
      </div>

      {/* Surrounding nodes */}
      {nodes.map((node, i) => (
        <div
          key={node.label}
          className="af-fade-in absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1.5"
          style={{ left: `${node.x}%`, top: `${node.y}%`, animationDelay: `${i * 0.08}s` }}
        >
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--af-border)] bg-[var(--af-bg)] text-[var(--af-accent)]"
            style={{ boxShadow: "var(--af-shadow)" }}
          >
            {node.icon}
          </div>
          <span className="whitespace-nowrap text-[11px] text-[var(--af-muted)]">{node.label}</span>
        </div>
      ))}
    </div>
  );
}
